/**
 * @module shop/product-details
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
// Core
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
// Interfaces
import { ProductReviewInterface } from '@core/aem/components/product-reviews/interface/product-review.interface';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { SearchOptions } from '@core/aem/components/product-reviews/interface/product-review.interface';

// Services
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ProductDetailsService } from '@experience/shop/services/product-details.service';
// Constants
import { PRODUCT_DETAILS_CONST } from '@experience/shop/product-details/product-details.constants';
import { takeUntil } from 'rxjs/operators';
import { ReviewsQuery } from '@experience/shop/product-details/models/review-query.model';
import { PlatformLocation } from '@angular/common';

/**
 * Product Specific Component initiator
 * @export
 * @class ProductSpecificationComponent
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'tmo-product-reviews-landing',
  templateUrl: './product-reviews-landing.component.html',
  styleUrls: [
    '../../../../../../style-sheets/components/page-titles.component.scss',
    '../../../../../../style-sheets/components/html-filter-sort.component.scss',
    '../../../../../../style-sheets/components/html-accordion.component.scss',
    '../../../../../../style-sheets/components/input-checkbox.component.scss',
    '../../../../../../style-sheets/components/input-checkbox.component.scss',
    '../../../../../../style-sheets/components/input-search-go.component.scss',
  ],
})
export class ProductReviewsLandingComponent implements OnInit, OnDestroy {
  /**
   * Boolean to  Panel Show for Sort Option
   */
  public sortPanelShow: boolean;
  /**
   * Boolean Panel Show for filter panel Option
   */
  public filterPanelShow: boolean;
  /**
   * Boolean to show search Panel
   */
  public searchPanelShow: boolean;
  /**
   * Boolean to show see all reviews cta
   */
  public seeAllCta: boolean;
  /**
   * Stores product title
   */
  public productDetailTitle: string;
  /**
   * Variable to review API response
   */
  public reviewContent: ProductReviewInterface;
  /**
   * Variable to store AEM content
   */
  public reviewsStaticContent: ConfigData;
  /**
   * To distroy the event OnDestroy life cycle
   */
  public ngUnsubscribe: Subject<void>;
  /**
   * Boolean to display limit of number of reviews to be shown on reviews landing page
   */
  public limit: boolean;
  /**
   * Variable to store type for reviews landing page
   */
  public type: string;

  /**
   * Variable to store searchQuery
   */
  public searchQuery: string;

  /**
   * Variable to store queryParams
   */
  public queryParams: ReviewsQuery;

  /**
   * Variable to store showMiniStars
   */
  public showMiniStars: boolean;

  /**
   * Variable to store filterRatings
   */
  public filterRatings: number[];

  /**
   * Variable to store selectedSort
   */
  public selectedSort: string;

  /**
   * Variable to store Seclected Device Family name
   */
  public selectedDevicefamilyName: string;

  /**
   * Variable to store filterData of type SearchOptions
   */
  public filterData: SearchOptions;

  constructor(
    private stateStoreUtils: StateStoreUtilsService,
    private productDetailsService: ProductDetailsService,
    private location: PlatformLocation,
    public routeSnapshot: ActivatedRoute
  ) {
    this.ngUnsubscribe = new Subject<void>();
    this.seeAllCta = false;
    this.limit = false;
    this.showMiniStars = true;
    this.filterRatings = [];
    this.selectedSort = '';
    this.type = PRODUCT_DETAILS_CONST.DETAILS;
  }

  /**
   * Component lifecycle hook to initialize the component
   * @memberof ProductReviewsLandingComponent
   */
  public ngOnInit(): void {
    this.productDetailsService
      .getProductReviewsDetailsContent()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((content: ConfigData) => {
        this.reviewsStaticContent = content;
      });
    this.selectedDevicefamilyName = this.stateStoreUtils.getSelectedDeviceFamilyName();
    this.queryParams = new ReviewsQuery(PRODUCT_DETAILS_CONST.DETAILS);
  }

  /**
   * Unsubscribes for all subscriptions
   * @memberof ProductReviewsLandingComponent
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Function to set search query params
   * @memberof ProductReviewsLandingComponent
   */
  public setSeachQuery(): void {
    this.queryParams = new ReviewsQuery(
      PRODUCT_DETAILS_CONST.DETAILS,
      'search',
      this.searchQuery,
      this.queryParams.queryParams
    );
  }

  /**
   * Function to set sortByReviews query params
   * @memberof ProductReviewsLandingComponent
   */
  public sortByReviews(sortBy: string): void {
    this.queryParams = new ReviewsQuery(
      PRODUCT_DETAILS_CONST.DETAILS,
      'sortBy',
      sortBy,
      this.queryParams.queryParams
    );
  }

  /**
   * Function to navigate to PDP
   * return void
   */
  public goToPDP(): void {
    this.location.back();
  }

  /**
   * Function to set FilterByRating query params
   * @memberof ProductReviewsLandingComponent
   */
  public setFilterByRating(event: Event, rating: number): void {
    const key = 'checked';
    if (event && event.srcElement[key]) {
      this.filterRatings.push(rating);
    } else {
      const ratingIndex = this.filterRatings.indexOf(rating);
      this.filterRatings.splice(ratingIndex, 1);
    }
    const filterQuery = this.filterRatings.toString();
    this.queryParams = new ReviewsQuery(
      PRODUCT_DETAILS_CONST.DETAILS,
      'filter',
      filterQuery,
      this.queryParams.queryParams
    );
  }

  /**
   * Function to set FilterData count
   * @param event of type SearchOptions
   */
  public setFilterData(event: SearchOptions): void {
    this.filterData = event;
  }
}
